/**
 * Which references a statement import may actually write.
 *
 * `transactions.reference` is UNIQUE, so every row of a bank statement is competing for a value that
 * exactly one row can have. The rules live here rather than in the sheet because the interesting part
 * is not any single row's verdict but the TRANSITIONS between them — a block that has to clear when
 * the row holding a value moves off it, an edit that must never dislodge a row that is not being
 * edited — and those are only observable by judging the whole list at once.
 */

/**
 * Why a row's CURRENT reference cannot be written:
 * - `db`    — a transaction is already stored under it
 * - `batch` — another row of THIS statement is being imported under it
 * - `blank` — the statement gave this row no reference, and one blank row is all the unique
 *             constraint would ever accept
 */
export type RefIssue = 'db' | 'batch' | 'blank';

/** The stored transaction a flagged row collides with — shown so a re-import can be told from a reuse */
export interface ExistingTxn {
  amount: number;
  currency: string;
  transactionAt: string;
}

/** All `applyRefIssues` needs of the references already in the table — a `Set` or `Map` both fit */
export interface RefLookup {
  has(reference: string): boolean;
}

/** The part of a review row this module reads and writes */
export interface ReferenceRow {
  /** Row identity — `reference` is editable and cannot serve as one */
  rowId: string;
  /** The value that will be inserted, possibly retyped by the admin */
  reference: string;
  /** The reference as parsed, which is what an untouched row still carries */
  originalReference: string;
  /** Why the CURRENT reference cannot be inserted — `null` when the row is importable */
  refIssue: RefIssue | null;
  /** Whether the admin may retype this reference; see `applyRefIssues` for why it never closes */
  refEditable: boolean;
}

/**
 * Judge every row against the stored references and against each other, in one pass.
 *
 * A value is claimable once, and only by a row that can use it: blank is never writable, nor is a
 * value the table already holds. A BLOCKED row claims nothing — its reference is a value no insert
 * will consume, so reserving it would let one dead row hold another hostage.
 *
 * Rows still carrying their PARSED reference are judged first. That ordering is what stops one
 * admin's edit from taking a value out from under a row that is not being edited: the untouched row
 * keeps what the bank gave it and the edit is the side that has to give way.
 *
 * `refEditable` opens the moment a row is blocked and never closes again. A retyped value goes
 * unique long before the admin has finished typing it, and an affordance that withdrew itself right
 * there would take the half-typed input away mid-keystroke.
 *
 * Returns the SAME array when no verdict moved, so it is safe to run from an effect.
 */
export function applyRefIssues<T extends ReferenceRow>(rows: T[], stored: RefLookup): T[] {
  const claimed = new Set<string>();
  const issues = new Map<string, RefIssue | null>();

  const judge = (row: T) => {
    const issue: RefIssue | null =
      row.reference === '' ? 'blank'
        : stored.has(row.reference) ? 'db'
          : claimed.has(row.reference) ? 'batch'
            : null;
    if (!issue) claimed.add(row.reference);
    issues.set(row.rowId, issue);
  };

  for (const row of rows) if (row.reference === row.originalReference) judge(row);
  for (const row of rows) if (row.reference !== row.originalReference) judge(row);

  let changed = false;
  const next = rows.map(row => {
    const issue = issues.get(row.rowId) ?? null;
    const refEditable = row.refEditable || issue !== null;
    if (row.refIssue === issue && row.refEditable === refEditable) return row;
    changed = true;
    return { ...row, refIssue: issue, refEditable };
  });

  return changed ? next : rows;
}
